import { create } from "zustand";
import { api, ApiError } from "../services/api.ts";

export interface TmaUser {
  id: number;
  username: string;
  email: string | null;
  telegram_id: number | null;
  plan: string;
  preferred_currency: string;
  active_currencies?: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  token: string | null;
  user: TmaUser | null;
  error: string | null;
  authenticate: (initData: string) => Promise<void>;
  setAuth: (access: string, refresh: string, user: TmaUser | null) => void;
  logout: () => Promise<void>;
}

function loadUser(): TmaUser | null {
  try {
    const raw = localStorage.getItem("tma_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearStoredSession(): void {
  localStorage.removeItem("tma_token");
  localStorage.removeItem("tma_refresh_token");
  localStorage.removeItem("tma_user");
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: !!localStorage.getItem("tma_token"),
  isAuthenticating: false,
  token: localStorage.getItem("tma_token"),
  user: loadUser(),
  error: null,

  authenticate: async (initData: string) => {
    set({ isAuthenticating: true, error: null });

    if (!initData || initData.trim().length === 0) {
      const errorMessage =
        "Приложение должно быть открыто через Telegram бота. Откройте бота в Telegram и нажмите Menu Button.";
      console.error("❌ Authentication failed: empty initData");
      set({
        isAuthenticated: false,
        isAuthenticating: false,
        token: null,
        error: errorMessage,
      });
      return;
    }

    console.log(
      "🔐 Authenticating with initData (length:",
      initData.length,
      ")",
    );

    try {
      const response = await api.post<{ access: string; refresh: string; user: TmaUser }>(
        "/auth/telegram/",
        { init_data: initData },
        { auth: false, retryAuth: false },
      );
      const { access, refresh, user } = response;
      localStorage.setItem("tma_token", access);
      localStorage.setItem("tma_refresh_token", refresh);
      if (user) localStorage.setItem("tma_user", JSON.stringify(user));
      console.log("✅ Authentication successful");
      set({
        isAuthenticated: true,
        isAuthenticating: false,
        token: access,
        user: user ?? null,
        error: null,
      });
    } catch (error) {
      console.error("❌ Authentication failed:", error);
      clearStoredSession();

      let errorMessage = "Ошибка аутентификации. ";

      if (error instanceof ApiError) {
        if (error.status === 401) {
          errorMessage +=
            "Неверные данные Telegram. Попробуйте переоткрыть приложение через бота.";
        } else if (error.status === 500) {
          errorMessage += "Ошибка сервера. Попробуйте позже.";
        } else {
          errorMessage += error.detail || "Неизвестная ошибка.";
        }
      } else {
        errorMessage += "Проверьте подключение к интернету.";
      }

      set({
        isAuthenticated: false,
        isAuthenticating: false,
        token: null,
        user: null,
        error: errorMessage,
      });
    }
  },

  setAuth: (access: string, refresh: string, user: TmaUser | null) => {
    localStorage.setItem("tma_token", access);
    if (refresh) localStorage.setItem("tma_refresh_token", refresh);
    if (user) localStorage.setItem("tma_user", JSON.stringify(user));
    set({ isAuthenticated: true, token: access, user, error: null });
  },

  logout: async () => {
    const refresh = localStorage.getItem("tma_refresh_token");
    try {
      if (refresh) {
        await api.post("/auth/logout/", { refresh });
      }
    } catch {
      // Preserve offline logout and idempotency for an already revoked token.
    } finally {
      clearStoredSession();
      set({ isAuthenticated: false, token: null, user: null, error: null });
    }
  },
}));

if (typeof window !== "undefined") {
  window.addEventListener("tarifdata:session-expired", () => {
    useAuthStore.setState({
      isAuthenticated: false,
      isAuthenticating: false,
      token: null,
      user: null,
      error: "Session expired",
    });
  });
}
